import { FEATURES, validateFamiliar, type Feature } from "@hexling/core";
import {
  Button,
  Dialog,
  Panel,
  SelectField,
  Spinner,
  StatusMessage,
  Switch,
  TextField,
} from "@hexling/ui";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PageTitle } from "../../components/Layout";
import {
  useLastSynced,
  useMe,
  useProfile,
  useServices,
  useSettingsEditor,
  useSignOut,
} from "../../data/AppData";
import { errorMessage } from "../../lib/api";
import { formatDay } from "../../lib/dates";
import { FAMILIARS } from "./familiars";

const FEATURE_COPY: Record<Feature, [string, string]> = {
  lunar: ["Moon phases", "The moon on Today and phases in the calendar"],
  sabbats: ["Sabbats", "The wheel of the year"],
  eclipses: ["Eclipses", "Solar and lunar eclipses in the calendar"],
  retrogrades: ["Retrogrades", "Mercury retrograde periods"],
  tarot: ["Tarot", "Card of the day and spreads"],
  astrology: ["Astrology", "Signs, planets, houses and aspects"],
  plants: ["Herbs & plants", "The plant shelf in the library"],
  crystals: ["Crystals", "The crystal shelf"],
  oils: ["Oils", "Essential and carrier oils"],
  recipes: ["Ritual recipes", "Incense, teas, baths and more"],
  lessons: ["Lessons", "Courses for learning the craft"],
};

export function SettingsPage() {
  const profile = useProfile().data;
  if (!profile) return <Spinner />;
  return (
    <>
      <PageTitle title="Settings" subtitle="Make Hexling fit your practice." />
      <div className="settings">
        <FamiliarSection />
        <AppearanceSection />
        <PracticeSection />
        <AccountSection />
        <AboutSection />
      </div>
    </>
  );
}

function FamiliarSection() {
  const { user } = useServices();
  const current = useProfile().data!.familiar;
  const [typeSlug, setTypeSlug] = useState(current?.typeSlug ?? "black-cat");
  const [name, setName] = useState(current?.name ?? "");
  const [customType, setCustomType] = useState(current?.customType ?? "");
  const [errors, setErrors] = useState<string[]>([]);
  const [status, setStatus] = useState("");

  const save = async () => {
    const familiar = {
      typeSlug,
      name: name.trim(),
      customType: typeSlug === "custom" ? customType.trim() : null,
    };
    const problems = validateFamiliar(familiar);
    setErrors(problems);
    if (problems.length) return;
    await user.setFamiliar(familiar);
    setStatus(`${familiar.name} has settled in.`);
  };

  return (
    <Panel aria-labelledby="familiar-heading">
      <h2 id="familiar-heading">Your familiar</h2>
      <p className="hx-muted">A small companion who greets you on the Today page.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <fieldset className="familiar-grid">
          <legend className="hx-label">Choose a creature</legend>
          {FAMILIARS.map((f) => (
            <label key={f.slug} className="familiar-tile">
              <input
                type="radio"
                name="familiar"
                value={f.slug}
                checked={typeSlug === f.slug}
                onChange={() => setTypeSlug(f.slug)}
              />
              <span className="familiar-tile__emoji" aria-hidden="true">
                {f.emoji}
              </span>
              <span>{f.name}</span>
            </label>
          ))}
        </fieldset>
        {typeSlug === "custom" && (
          <TextField
            label="What kind of creature?"
            placeholder="Axolotl, moth, tiny dragon…"
            value={customType}
            onChange={(e) => setCustomType(e.target.value)}
            maxLength={40}
          />
        )}
        <TextField
          label="Name"
          placeholder="Salem"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
          error={errors.join(" ") || null}
        />
        <Button type="submit">Save familiar</Button>
        {current && (
          <Button
            variant="ghost"
            onClick={async () => {
              await user.setFamiliar(null);
              setName("");
              setStatus("Your familiar has wandered off for now.");
            }}
          >
            Remove familiar
          </Button>
        )}
        <StatusMessage>{status}</StatusMessage>
      </form>
    </Panel>
  );
}

function AppearanceSection() {
  const { settings: s, update } = useSettingsEditor();
  return (
    <Panel aria-labelledby="appearance-heading">
      <h2 id="appearance-heading">Appearance</h2>
      <fieldset className="theme-choice">
        <legend className="hx-label">Theme</legend>
        <label className="theme-option">
          <input
            type="radio"
            name="theme"
            checked={s.theme === "clarity"}
            onChange={() => update({ theme: "clarity" })}
          />
          <span>
            <strong>Clarity</strong>
            <span className="hx-field__hint">
              Highest contrast and the most readable type. Recommended.
            </span>
          </span>
        </label>
        <label className="theme-option">
          <input
            type="radio"
            name="theme"
            checked={s.theme === "midnight"}
            onChange={() => update({ theme: "midnight" })}
          />
          <span>
            <strong>Midnight</strong>
            <span className="hx-field__hint">
              Deeper and moodier, with gold-leaf details and starlight. Text stays readable.
            </span>
          </span>
        </label>
      </fieldset>
      <Switch
        label="Reduce motion"
        description="Turn off animations, even if your device allows them"
        checked={s.reducedMotion}
        onChange={(v) => update({ reducedMotion: v })}
      />
    </Panel>
  );
}

function PracticeSection() {
  const { settings: s, update } = useSettingsEditor();
  return (
    <Panel aria-labelledby="practice-heading">
      <h2 id="practice-heading">Your practice</h2>
      <SelectField
        label="Hemisphere"
        hint="Flips the wheel of the year and the moon's lit side."
        value={s.hemisphere}
        onChange={(e) => update({ hemisphere: e.target.value as "northern" | "southern" })}
        options={[
          { value: "northern", label: "Northern" },
          { value: "southern", label: "Southern" },
        ]}
      />
      <SelectField
        label="Sabbat dates"
        value={s.sabbatDateStyle}
        onChange={(e) =>
          update({ sabbatDateStyle: e.target.value as "traditional" | "astronomical" })
        }
        options={[
          { value: "traditional", label: "Traditional (Samhain on October 31)" },
          { value: "astronomical", label: "Astronomical (exact solar positions)" },
        ]}
      />
      <h3>Show in Hexling</h3>
      {FEATURES.map((f) => (
        <Switch
          key={f}
          label={FEATURE_COPY[f][0]}
          description={FEATURE_COPY[f][1]}
          checked={s.features[f]}
          onChange={(v) => update({ features: { [f]: v } })}
        />
      ))}
    </Panel>
  );
}

function AccountSection() {
  const { sync } = useServices();
  const { settings: s, update } = useSettingsEditor();
  const me = useMe().data;
  const last = useLastSynced().data;
  const { signOut, busy } = useSignOut();
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [confirmErase, setConfirmErase] = useState(false);
  const { store } = useServices();

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await sync.sync();
      setStatus(`Synced: sent ${r.pushed}, received ${r.pulled}.`);
    } catch (err) {
      setStatus(errorMessage(err));
    } finally {
      setSyncing(false);
      await qc.invalidateQueries();
    }
  };

  return (
    <Panel aria-labelledby="account-heading">
      <h2 id="account-heading">Account & sync</h2>
      {me?.user ? (
        <>
          <p>
            Signed in as <strong>{me.user.email}</strong>.
          </p>
          <Switch
            label="Sync this device"
            description="Keep notes, lists, bookmarks and settings the same on all your devices"
            checked={s.syncEnabled}
            onChange={(v) => update({ syncEnabled: v })}
          />
          {s.syncEnabled && (
            <p className="hx-muted">
              {last
                ? `Last synced ${formatDay(new Date(last), { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.`
                : "Not synced yet."}{" "}
              <Button variant="ghost" size="small" onClick={syncNow} busy={syncing}>
                Sync now
              </Button>
            </p>
          )}
          {me.isStaff && (
            <p>
              <Link to="/admin">Open the admin area</Link>
            </p>
          )}
          <Button variant="secondary" onClick={signOut} busy={busy}>
            Sign out
          </Button>
        </>
      ) : (
        <>
          <p>
            Hexling works fully without an account. Sign in to back up your grimoire and sync it
            between devices.
          </p>
          <Link to="/sign-in" className="hx-button hx-button--primary">
            Sign in or create an account
          </Link>
        </>
      )}
      <StatusMessage>{status}</StatusMessage>
      <hr className="rule" />
      <Button variant="danger" size="small" onClick={() => setConfirmErase(true)}>
        Erase data on this device
      </Button>
      <Dialog
        open={confirmErase}
        title="Erase everything on this device?"
        onClose={() => setConfirmErase(false)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmErase(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                await store.clear();
                setConfirmErase(false);
                await qc.invalidateQueries();
                setStatus("This device has been cleared.");
              }}
            >
              Erase this device
            </Button>
          </>
        }
      >
        <p>
          Notes, lists, bookmarks, settings and the downloaded library will be removed from this
          device. Anything already synced stays in your account.
        </p>
      </Dialog>
    </Panel>
  );
}

function AboutSection() {
  return (
    <Panel aria-labelledby="about-heading">
      <h2 id="about-heading">About & credits</h2>
      <p>
        Hexling is a reference and journal, not medical, veterinary or legal advice. Always check
        safety information and consult a professional before ingesting herbs or using oils.
      </p>
      <ul className="credits">
        <li>Pet toxicity data: ASPCA Animal Poison Control plant database.</li>
        <li>
          Tarot meanings: the Tarotoo tarot dataset by <a href="https://tarotoo.com">tarotoo.com</a>
          , used under CC BY 4.0.
        </li>
        <li>Moon, eclipse and sabbat times: calculated with Astronomy Engine (MIT).</li>
        <li>
          Typefaces: Atkinson Hyperlegible Next (Braille Institute) and IM Fell English (Igino
          Marini), under the SIL Open Font License.
        </li>
      </ul>
    </Panel>
  );
}
